import { isNil } from 'lodash-es'
import { ComponentProps, PropsWithChildren } from 'react'

import { RoomIcon } from '@/entities/room'
import { cn } from '@/shared/lib'

type HeadingProps = {
  className?: string
}

export const Heading = ({
  className,
  children,
}: PropsWithChildren<HeadingProps>) => {
  return (
    <h2 className={cn('typo-headline2 font-semibold text-black', className)}>
      {children}
    </h2>
  )
}

type SubHeadingProps = {
  className?: string
}

export const SubHeading = ({
  className,
  children,
}: PropsWithChildren<SubHeadingProps>) => {
  return (
    <h3
      className={cn(
        'typo-body1-reading font-semibold text-gray-900',
        className,
      )}
    >
      {children}
    </h3>
  )
}

type ListProps = {
  spacing: 'small' | 'medium' | 'large'
  variant?: 'solid' | 'dashed'
  borderColor?: 'gray' | 'light-gray'
  className?: string
}

export const List = ({
  spacing,
  variant = 'solid',
  borderColor = 'light-gray',
  className,
  children,
}: PropsWithChildren<ListProps>) => {
  return (
    <ul
      className={cn(
        spacing === 'small' && '*:py-3',
        spacing === 'medium' && '*:py-4',
        spacing === 'large' && '*:py-5',
        variant === 'dashed' && '*:border-dashed',
        borderColor === 'gray' && '*:border-gray-200',
        borderColor === 'light-gray' && '*:border-gray-50',
        className,
      )}
    >
      {children}
    </ul>
  )
}

type ItemProps = {
  className?: string
}

export const Item = ({ className, children }: PropsWithChildren<ItemProps>) => {
  return (
    <li
      className={cn(
        'border-b first:pt-0 last:border-none last:pb-0',
        'flex items-center',
        className,
      )}
    >
      {children}
    </li>
  )
}

type LabelProps = {
  className?: string
  icon?: ComponentProps<typeof RoomIcon>['name']
  size?: 'small' | 'medium'
  iconClassName?: string
}

export const Label = ({
  icon,
  size = 'medium',
  className,
  iconClassName,
  children,
}: PropsWithChildren<LabelProps>) => {
  if (isNil(icon)) {
    return <span className={className}>{children}</span>
  }

  return (
    <div
      className={cn('flex items-center gap-3', size === 'small' && 'gap-1.5')}
    >
      <RoomIcon
        name={icon}
        className={cn('text-black', iconClassName)}
        size={size === 'small' ? 20 : 24}
      />
      <span className={className}>{children}</span>
    </div>
  )
}

type ValueProps = {
  className?: string
}

export const Value = ({
  className,
  children,
}: PropsWithChildren<ValueProps>) => {
  return <span className={cn('font-normal', className)}>{children}</span>
}
